import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";

interface Props {
  open: boolean;
  // The auto-derived label shown as the default. Used verbatim when the
  // user dismisses without editing, and pre-fills the input so they can
  // tweak instead of retyping from scratch.
  defaultLabel: string;
  onSubmit: (label: string) => void;
  onCancel: () => void;
}

/**
 * Quick "Label this node" prompt that appears when the user drops a
 * highlight onto the canvas. Submit uses the typed label; Cancel /
 * close uses the autoLabel-derived default so the rectangle always
 * gets *some* short body rather than the full annotation paragraph.
 *
 * Enter submits, Esc cancels (matches MUI Dialog conventions).
 */
const LabelRectModal = ({ open, defaultLabel, onSubmit, onCancel }: Props) => {
  const [value, setValue] = useState(defaultLabel);

  // Reset the field whenever the modal re-opens with a new default
  // (e.g., second consecutive drop with different highlight text).
  useEffect(() => {
    if (open) setValue(defaultLabel);
  }, [open, defaultLabel]);

  const commit = () => {
    const next = value.trim();
    onSubmit(next || defaultLabel);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Label this node</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The full annotation is preserved — double-click the node to read it.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          onKeyDown={handleKey}
          placeholder={defaultLabel}
          inputProps={{ maxLength: 80 }}
          helperText="Press Enter to save, Esc to use the default."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Use default</Button>
        <Button variant="contained" onClick={commit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabelRectModal;
